import React, { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';

import RatePlanForm from '~/components/Common/Forms/RatePlanForm';
import { buttonMessages, buttonStates } from '~/components/Common/Forms/states/submitButton';

import {
  ratePlanFormDataQueryKeys,
  useEditRatePlanFormData,
  useNewRatePlanFormData,
} from '~/hooks/apis/reservation/useRatePlanFormData';

const RATE_PLAN_FORM_ID = 'rate-plan-dialog-form';

interface Props {
  open: boolean;
  onClose: () => void;
  // Called after a successful save or delete, so the caller can refresh its rate plans list.
  onChanged: () => void;
  vendorId: string;
  // When set, the dialog edits this rate plan instead of creating a new one.
  ratePlanId?: string;
}

export default function RatePlanDialog({ open, onClose, onChanged, vendorId, ratePlanId }: Props) {
  const isEditMode = !!ratePlanId;
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Both hooks are keyed on stable ids (not gated on `open`), so `data` survives the dialog's close transition
  // instead of dropping to undefined and collapsing the form before the close animation finishes.
  const newRatePlanQuery = useNewRatePlanFormData(isEditMode ? undefined : vendorId);
  const editRatePlanQuery = useEditRatePlanFormData(isEditMode ? vendorId : undefined, ratePlanId);
  const { data, isLoading, error } = isEditMode ? editRatePlanQuery : newRatePlanQuery;

  // Remounts RatePlanForm on each open so stale state from a prior session doesn't linger.
  const [openCount, setOpenCount] = useState(0);
  const [saveLabel, setSaveLabel] = useState(buttonMessages[buttonStates.default]);
  useEffect(() => {
    if (open) {
      setOpenCount((count) => count + 1);
      setSaveLabel(buttonMessages[buttonStates.default]);
    }
  }, [open]);

  const isSaving = saveLabel === buttonMessages[buttonStates.saving];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {isEditMode ? 'Edit rate plan' : 'Create rate plan'}
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
          {isEditMode
            ? 'Update pricing, policies, and schedules for this rate plan.'
            : 'Set up pricing, policies, and schedules for a new rate plan on this vendor.'}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ overflowX: 'hidden' }}>
        {isLoading && <CircularProgress />}
        {error && <p>Unable to load rate plan form.</p>}
        {data && (
          <Box sx={{ mt: 3 }}>
            <RatePlanForm
              key={openCount}
              schema={data.schema}
              ratePlan={data.ratePlan ?? { id_salesforce_external: vendorId }}
              ratePlanId={ratePlanId}
              packagedRatePlans={data.packagedRatePlans}
              vendorId={vendorId}
              formId={RATE_PLAN_FORM_ID}
              onSaveStateChange={setSaveLabel}
              onSaved={() => {
                enqueueSnackbar(isEditMode ? 'Rate plan saved' : 'Rate plan created', { variant: 'success' });
                queryClient.invalidateQueries({ queryKey: ratePlanFormDataQueryKeys.all });
                onChanged();
                onClose();
              }}
              onDeleted={() => {
                enqueueSnackbar('Rate plan deleted', { variant: 'success' });
                onChanged();
                onClose();
              }}
            />
          </Box>
        )}
      </DialogContent>
      {data && (
        <DialogActions sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper' }}>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={RATE_PLAN_FORM_ID}
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saveLabel}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
